import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { OnboardingModal } from '../components/OnboardingModal';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { isProfileComplete } from '../utils/profileValidation';

/**
 * Dashboard Home Page
 * Access: All authenticated users
 *
 * Landing page after sign-in, shows role-appropriate quick actions and overview.
 */

export function DashboardHome() {
  const { user, hasRole } = useAuth();
  const { data: profile, isLoading } = useProfile();
  const [showOnboarding, setShowOnboarding] = useState(false);

  const displayName = profile?.preferredName || user?.email || 'there';

  // Check if onboarding is needed when profile loads
  useEffect(() => {
    if (!isLoading && profile && !isProfileComplete(profile)) {
      setShowOnboarding(true);
    }
  }, [isLoading, profile]);

  return (
    <>
      <OnboardingModal
        isOpen={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
      />

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-2 text-text-dark">
            Welcome back, {displayName}!
          </h1>
          <p className="text-lg mb-8 text-text-secondary">
            Your integrative approach to metabolic health
          </p>

          {/* Admin Quick Actions */}
          {hasRole('admin') && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-text-dark">
                Admin Tools
              </h2>
              <div className="grid gap-6 md:grid-cols-3">
                <Link
                  to="/dashboard/users"
                  className="card hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-3">👥</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    User Management
                  </h3>
                  <p className="text-text-secondary">
                    Manage users, roles, and permissions
                  </p>
                </Link>

                <Link
                  to="/dashboard/settings"
                  className="card hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-3">⚙️</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    System Settings
                  </h3>
                  <p className="text-text-secondary">
                    Configure system-wide settings
                  </p>
                </Link>

                <div className="card">
                  <div className="text-4xl mb-3">📊</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    Analytics
                  </h3>
                  <p className="text-text-secondary">
                    View system-wide metrics
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Coach Quick Actions */}
          {hasRole('coach') && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-text-dark">
                Coach Tools
              </h2>
              <div className="grid gap-6 md:grid-cols-3">
                <Link
                  to="/dashboard/clients"
                  className="card hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-3">👤</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    My Clients
                  </h3>
                  <p className="text-text-secondary">
                    View and manage client progress
                  </p>
                </Link>

                <Link
                  to="/dashboard/assessments"
                  className="card hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-3">📋</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    Assessments
                  </h3>
                  <p className="text-text-secondary">
                    Review client assessments
                  </p>
                </Link>

                <div className="card">
                  <div className="text-4xl mb-3">📅</div>
                  <h3 className="text-lg font-semibold mb-2 text-text-dark">
                    Schedule
                  </h3>
                  <p className="text-text-secondary">Manage appointments</p>
                </div>
              </div>
            </div>
          )}

          {/* User/Everyone Quick Actions */}
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-text-dark">
              My Health
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              <Link
                to="/dashboard/health"
                className="card hover:shadow-md transition-shadow"
              >
                <div className="text-4xl mb-3">❤️</div>
                <h3 className="text-lg font-semibold mb-2 text-text-dark">
                  Health Overview
                </h3>
                <p className="text-text-secondary">
                  Track your daily health metrics
                </p>
              </Link>

              <Link
                to="/dashboard/metrics"
                className="card hover:shadow-md transition-shadow"
              >
                <div className="text-4xl mb-3">📊</div>
                <h3 className="text-lg font-semibold mb-2 text-text-dark">
                  Metrics & Trends
                </h3>
                <p className="text-text-secondary">View detailed analytics</p>
              </Link>

              <Link
                to="/dashboard/profile"
                className="card hover:shadow-md transition-shadow"
              >
                <div className="text-4xl mb-3">👤</div>
                <h3 className="text-lg font-semibold mb-2 text-text-dark">
                  My Profile
                </h3>
                <p className="text-text-secondary">Manage account settings</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
